import { useEffect, useState } from "react";
import axios from "axios";
import { API, fmtErr } from "../lib/pos";
import { Mail, Send, RefreshCw, Save, ExternalLink } from "lucide-react";

/**
 * SMTP API — manager-only page that owns the Brevo integration.
 *   • Brevo API key (masked)
 *   • Live sender picker fetched from Brevo `/v3/senders?active=true`
 *   • "Send test email" hits Brevo `/v3/smtp/email` (sendTransacEmail) using
 *     either the just-typed key or the saved one.
 */
export default function SmtpApi() {
  const [key, setKey] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [senderEmail, setSenderEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [senders, setSenders] = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [savedFlag, setSavedFlag] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingSend, setTestingSend] = useState(false);
  const [testMsg, setTestMsg] = useState(null);

  // Load current settings on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/settings/bill`);
        setKey(r.data.brevo_api_key || "");
        setSenderEmail(r.data.brevo_sender_email || "");
        setSenderName(r.data.brevo_sender_name || "");
      } catch (e) { console.warn("Failed loading settings", e); }
    })();
  }, []);

  const fetchSenders = async () => {
    setLoadingSenders(true);
    setTestMsg(null);
    try {
      // Send the typed key if the manager just edited it — else backend uses stored.
      const params = keyTouched && key ? { brevo_api_key: key } : {};
      const r = await axios.get(`${API}/mail/senders`, { params });
      setSenders(r.data.senders || []);
      if (!r.data.senders?.length) {
        setTestMsg({ ok: false, text: "Brevo returned 0 active senders. Verify one at app.brevo.com first." });
      }
    } catch (e) { setTestMsg({ ok: false, text: fmtErr(e) }); }
    finally { setLoadingSenders(false); }
  };

  const save = async () => {
    setSaving(true); setSavedFlag(false); setTestMsg(null);
    try {
      const payload = { brevo_sender_email: senderEmail || "", brevo_sender_name: senderName || "" };
      if (keyTouched) payload.brevo_api_key = key;
      await axios.put(`${API}/settings/bill`, payload);
      setSavedFlag(true);
      setKeyTouched(false);
      setTimeout(() => setSavedFlag(false), 2200);
    } catch (e) { setTestMsg({ ok: false, text: fmtErr(e) }); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTestingSend(true); setTestMsg(null);
    try {
      const body = keyTouched && key ? { brevo_api_key: key } : {};
      const r = await axios.post(`${API}/settings/bill/test-email`, body);
      setTestMsg({ ok: true, text: `Sent to ${r.data.to} · id ${(r.data.message_id || "").slice(0, 28)}` });
    } catch (e) { setTestMsg({ ok: false, text: fmtErr(e) }); }
    finally { setTestingSend(false); }
  };

  const onPickSender = (email) => {
    const s = senders.find((x) => x.email === email);
    setSenderEmail(email);
    setSenderName(s?.name || "");
  };

  return (
    <div className="settings-page smtp-api" data-testid="smtp-api-page">
      <div className="section-line" style={{ marginBottom: 6 }}>
        <h2><Mail size={18} style={{ verticalAlign: "-3px", marginRight: 8 }} />SMTP API · Brevo</h2>
      </div>
      <p className="muted" style={{ marginTop: 0 }}>
        This page owns the Brevo transactional email integration. The key + sender picked here drive
        every automated email — password reset, daily station summary, and any future notifier.
      </p>

      <div className="settings-grid" style={{ marginTop: 18, display: "grid", gap: 20 }}>
        <label className="field wide">
          <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
            <b>Brevo API key</b>
            <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer" style={{ color: "#c2185b", fontSize: 12 }}>
              get one <ExternalLink size={11} style={{ verticalAlign: -1 }} />
            </a>
          </span>
          <input
            data-testid="smtp-api-key-input"
            type="password"
            autoComplete="off"
            spellCheck={false}
            value={key}
            placeholder="xkeysib-…"
            onFocus={() => { if (!keyTouched) { setKey(""); setKeyTouched(true); } }}
            onChange={(e) => setKey(e.target.value)}
          />
          <small style={{ color: "var(--muted)", marginTop: 4, fontSize: 11 }}>
            Focus + type to overwrite. Leave blank + save to keep the current value.
          </small>
        </label>

        <div className="field wide">
          <span style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
            <b>Verified sender</b>
            <button
              type="button"
              data-testid="smtp-api-refresh-senders"
              className="outline"
              disabled={loadingSenders}
              onClick={fetchSenders}
              style={{ padding: "4px 10px", fontSize: 12 }}
            >
              <RefreshCw size={12} style={{ marginRight: 4, verticalAlign: -1 }} />
              {loadingSenders ? "Loading…" : (senders.length ? `Refresh (${senders.length})` : "Fetch from Brevo")}
            </button>
          </span>
          {senders.length > 0 ? (
            <select
              data-testid="smtp-api-sender-select"
              className="filter-select"
              style={{ width: "100%" }}
              value={senderEmail}
              onChange={(e) => onPickSender(e.target.value)}
            >
              <option value="">— Use default (MAIL_FROM env) —</option>
              {senders.map((s) => (
                <option key={s.id || s.email} value={s.email}>
                  {s.name ? `${s.name} <${s.email}>` : s.email}
                </option>
              ))}
            </select>
          ) : (
            <input
              data-testid="smtp-api-sender-fallback"
              value={senderEmail}
              onChange={(e) => onPickSender(e.target.value)}
              placeholder="Click 'Fetch from Brevo' to load your verified senders"
              disabled
            />
          )}
          {senderEmail && (
            <small data-testid="smtp-api-sender-current" style={{ color: "var(--green)", marginTop: 4, fontSize: 12 }}>
              ✓ Emails will be sent from <b>{senderName || senderEmail}</b> &lt;{senderEmail}&gt;
            </small>
          )}
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <button data-testid="smtp-api-save" className="primary" disabled={saving} onClick={save}>
            <Save size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {saving ? "Saving…" : "Save"}
          </button>
          <button data-testid="smtp-api-test" className="outline" disabled={testingSend} onClick={sendTest}>
            <Send size={14} style={{ marginRight: 6, verticalAlign: -2 }} />
            {testingSend ? "Sending…" : "Send test email"}
          </button>
          {savedFlag && <span data-testid="smtp-api-saved-flag" style={{ color: "var(--green)", fontSize: 12 }}>✓ Saved</span>}
        </div>

        {testMsg && (
          <p data-testid="smtp-api-test-msg" style={{ margin: 0, padding: "10px 14px", borderRadius: 4,
              color: testMsg.ok ? "var(--green)" : "var(--red)",
              background: testMsg.ok ? "rgba(63,169,120,0.1)" : "rgba(230,57,70,0.1)",
              border: `1px solid ${testMsg.ok ? "rgba(63,169,120,0.35)" : "rgba(230,57,70,0.35)"}`,
              fontSize: 12, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {testMsg.ok ? "✓ " : "✗ "}{testMsg.text}
          </p>
        )}
      </div>
    </div>
  );
}
